import React, { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { Paperclip, Send, X, FileText } from 'lucide-react';
import { useCreateCommentOnTicket } from '@/hooksApi';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';

interface Props {
  ticketId?: string;
  disabled: boolean;
  onHeightChange?: (height: number) => void;
}

const CommentForm = ({ ticketId, disabled, onHeightChange }: Props) => {
  const [files, setFiles] = useState<File[]>([]);
  const [text, setText] = useState('');

  const formRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { hasPermission } = usePermission();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: createComment, isLoading: isCreatingComment } = useCreateCommentOnTicket(
    ticketId ?? '',
  );

  const canSubmit = hasPermission(PermissionName.ADD_COMMENT);
  const isEmpty = !text.trim() && files.length === 0;
  const isDisabled = isEmpty || isCreatingComment || disabled || !canSubmit;

  const handleSend = () => {
    if (!text.trim() && files.length === 0) return;

    const formData = new FormData();
    formData.append('body', text.trim());
    files.forEach((file) => formData.append('attachments[]', file));

    createComment(formData, {
      onSuccess: () => {
        setText('');
        setFiles([]);
        if (textareaRef.current) textareaRef.current.style.height = 'auto';
        queryClient.invalidateQueries(['ticket-comments']);
        queryClient.invalidateQueries(['ticket']);
      },
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const maxTotal = 20 * 1024 * 1024; // 20MB
    const currentTotal = files.reduce((sum, f) => sum + f.size, 0);
    const newFiles = Array.from(e.target.files).filter((f) => {
      if (currentTotal + f.size > maxTotal) {
        message.error(String(t('file_exceeds_the_maximum_allowed_size', { fileName: f.name })));
        return false;
      }
      return true;
    });
    setFiles((prev) => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    if (formRef.current && onHeightChange) {
      onHeightChange(formRef.current.offsetHeight);
    }
  }, [text, files]);

  return (
    <div ref={formRef}>
      {/* File preview chips */}
      {files.length > 0 && (
        <div className='flex gap-2 mb-3 flex-wrap'>
          {files.map((file, i) => (
            <div
              key={i}
              className='flex items-center gap-1.5 bg-sd-muted rounded-lg px-2.5 py-1.5'
            >
              <FileText className='w-3.5 h-3.5 text-sd-muted-foreground' />
              <span className='text-[12px] text-sd-foreground truncate max-w-[140px]'>
                {file.name}
              </span>
              <button
                type='button'
                onClick={() => removeFile(i)}
                className='p-0 bg-transparent border-0 cursor-pointer'
              >
                <X className='w-3.5 h-3.5 text-sd-muted-foreground hover:text-sd-foreground' />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Composer bar */}
      <div className='flex items-center gap-3 rounded-2xl border border-solid border-sd-border px-4 py-3' style={{ backgroundColor: 'rgb(249,250,251)' }}>
        <input
          ref={fileInputRef}
          type='file'
          onChange={handleFileChange}
          className='hidden'
          multiple
          disabled={disabled || !canSubmit}
        />
        <button
          type='button'
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || !canSubmit}
          className='shrink-0 text-sd-muted-foreground hover:text-sd-foreground transition-colors bg-transparent border-0 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed p-0'
        >
          <Paperclip className='w-5 h-5' />
        </button>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            // Auto-resize
            const el = e.target;
            el.style.height = 'auto';
            el.style.height = `${Math.min(el.scrollHeight, 112)}px`;
          }}
          placeholder={String(t('type_a_message', { defaultValue: 'Type a message...' }))}
          rows={1}
          disabled={disabled || !canSubmit}
          className='flex-1 bg-transparent text-[14px] text-sd-foreground placeholder:text-sd-muted-foreground focus:outline-none resize-none max-h-28 leading-relaxed border-0 p-0'
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (!isDisabled) handleSend();
            }
          }}
        />

        <button
          type='button'
          onClick={handleSend}
          disabled={isDisabled}
          className='shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-sd-primary disabled:opacity-30 transition-opacity cursor-pointer border-0'
        >
          {isCreatingComment ? (
            <div className='w-4 h-4 rounded-full border-2 border-[rgb(23,23,23)] border-t-transparent animate-spin' />
          ) : (
            <Send className='w-4 h-4' style={{ color: 'rgb(23,23,23)' }} />
          )}
        </button>
      </div>
    </div>
  );
};

export default CommentForm;
